import React, { useCallback, useEffect, useState } from "react";
import { StudentDetailView } from "../../firestore/Types/StudentDetailSchema";
import { getStudentDetail, readUserGuide } from "../../firestore/Functions/accountFunctions";
import { loadStripe, Stripe as StripeType } from "@stripe/stripe-js";
import Stripe from "stripe";
import { getStripeCheckoutSession, getStripeCustomerSubscriptions } from "../../firestore/Functions/StripeFunctions";
import { useUtility } from "./UtilityContext";

const studentContext = React.createContext(
    {
        studentDetail: {} as StudentDetailView,
        stripePromise: {} as Promise<StripeType | null>,
        checkoutSession: {} as Stripe.Checkout.Session,
        stripeCustomerSubscriptions: undefined as Stripe.Subscription[] | undefined,
        showUserGuide: false,
        refreshStudentDetail: async () => {},
    }
        
);

export function useStudent() {
    return React.useContext(studentContext);
}

export function StudentProvider({ children }: { children: React.ReactNode }) {
    const [studentDetail, setStudentDetail] = useState({} as StudentDetailView);

    const [checkoutSession, setCheckoutSession] = useState<Stripe.Checkout.Session>({} as Stripe.Checkout.Session);

    const [stripeCustomerSubscriptions, setStripeCustomerSubscriptions] = useState<Stripe.Subscription[]>([] as Stripe.Subscription[]);

    const [showUserGuide, setShowUserGuide] = useState(false);
    
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);

    const { setNewToast } = useUtility();

    const refreshStudentDetail = async () => {
        const newStudentDetail = await getStudentDetail() as StudentDetailView;
        if (newStudentDetail) {
            setStudentDetail(newStudentDetail);
        }
    }

    const initializeStudent = useCallback( async () => {
        const newStudentDetail = await getStudentDetail().catch(
            (error) => {setNewToast({title: "Internal Error", message: error.message, toastType: "error"})}
        ) as StudentDetailView;
        if (newStudentDetail) {
            setStudentDetail(newStudentDetail);
        }

        if (newStudentDetail === undefined) {
            return;
        }

        if (newStudentDetail?.UserGuideCompleted === false) {
            setShowUserGuide(true);
            readUserGuide().catch((error) => {
                setNewToast({title: "Internal Error", message: error.message, toastType: "error"});
            });
            setTimeout(() => {
                setShowUserGuide(false);
            }, 200);
        }

        await getStripeCheckoutSession(newStudentDetail?.StripeCustomerId).then((session) => {
            setCheckoutSession(session);
        }).catch((error) => {
            setNewToast({title: "Internal Error", message: error.message, toastType: "error"});
        });

        await getStripeCustomerSubscriptions(newStudentDetail?.StripeCustomerId).then((subscriptions) => {
            setStripeCustomerSubscriptions(subscriptions);
        }).catch((error) => {
            setNewToast({title: "Internal Error", message: error.message, toastType: "error"});
        });
    }, [setNewToast]);
    
    useEffect(() => {
        initializeStudent();
    }, [initializeStudent]);

    const value = {
        studentDetail,
        stripePromise,
        checkoutSession,
        stripeCustomerSubscriptions,
        showUserGuide,
        refreshStudentDetail,
    };

    return (
        <studentContext.Provider value={value}>
            {children}
        </studentContext.Provider>
    )
}